import { useAxios } from "./useAxios"
import { Pagination } from "@/interfaces/Pagination"
import { useBot } from "./useBot"
import { PackageStatatusEnum } from "@/enums/packages/PackageStatusEnum"
import { Package } from "@/interfaces/packages/Package"
import { AcceptableInputValue } from "radix-vue/dist/TagsInput/TagsInputRoot"

const axios = useAxios()

export interface CreatePackageDTO {
  name: string
  paymentPlanIds: AcceptableInputValue[]
  status: PackageStatatusEnum
  media?: File[]
}

export interface UpdatePackageDTO {
  name: string
  paymentPlanIds: AcceptableInputValue[]
  status: PackageStatatusEnum
  media?: File[]
  deleteMediaIds?: number[]
}

export interface GetAllPackagesParams {
  page?: string
  perPage?: string
}

export function usePackage() {
  const { selectedBot } = useBot()

  const create = async (payload: CreatePackageDTO): Promise<Package> => {
    const formData = new FormData()
  
    for (const key in payload) {
      if (payload[key as keyof typeof payload] !== null && payload[key as keyof typeof payload] !== '') {
        const value = payload[key as keyof typeof payload]
  
        // Se o valor for um array de arquivos, fazer append de cada arquivo individualmente
        if (Array.isArray(value)) {
          value.forEach((file, index) => {
            if (file instanceof File) {
              formData.append(`${key}`, file)
            } else {
              formData.append(`${key}[${index}]`, value[index] as string)
            }
          })
        } else {
          // Se não for um array, simplesmente fazer o append normal
          formData.append(key, value as string | File)
        }
      }
    }
  
    const response = await axios.post(`/bots/${selectedBot.value?.id}/packages`, formData)
  
    return response.data
  }

  const update = async (id: number, payload: UpdatePackageDTO): Promise<void> => {
    const formData = new FormData()

    for (const key in payload) {
      if (payload[key as keyof typeof payload] !== null && payload[key as keyof typeof payload] !== '') {
        const value = payload[key as keyof typeof payload]
  
        // Se o valor for um array de arquivos, fazer append de cada arquivo individualmente
        if (Array.isArray(value)) {
          value.forEach((file, index) => {
            if (file instanceof File) {
              formData.append(`${key}`, file)
            } else {
              formData.append(`${key}[${index}]`, value[index] as string)
            }
          })
        } else {
          // Se não for um array, simplesmente fazer o append normal
          formData.append(key, value as string | File)
        }
      }
    }

    await axios.patch(`/bots/${selectedBot.value?.id}/packages/${id}`, formData)
  }

  const getAll = async (params?: GetAllPackagesParams): Promise<Pagination<Package>> => {
    if (params) {
      params = Object.fromEntries(
        Object.entries(params).filter(([param, value]) => !!value)
      )
    }

    const response = await axios.get(`/bots/${selectedBot.value?.id}/packages`, { params })

    return response.data
  }

  const getById = async (id: number): Promise<Package> => {
    const response = await axios.get(`/bots/${selectedBot.value?.id}/packages/${id}`)

    return response.data
  }

  const destroy = async (id: number): Promise<void> => {
    await axios.delete(`/bots/${selectedBot.value?.id}/packages/${id}`)
  }

  return {
    create,
    getAll,
    update,
    destroy,
    getById
  }
}